import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { Search, X, ChevronDown } from 'lucide-react';

export interface ComboItem {
  id: string;
  label: string;
  sequence?: number;
}

interface SearchableComboBoxProps {
  items: ComboItem[];
  valueId?: string;
  onChange: (id: string) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  loading?: boolean;
  error?: string;
  noResultsText?: string;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  position: relative;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${props => props.theme.textSecondary};
`;

const InputWrapper = styled.div<{ $disabled?: boolean, $hasError?: boolean, $isOpen?: boolean }>`
  display: flex;
  align-items: center;
  background-color: ${props => props.$disabled ? props.theme.background : props.theme.surface};
  border: 1px solid ${props => {
    if (props.$hasError) return props.theme.error;
    if (props.$isOpen) return props.theme.primary;
    return props.theme.border;
  }};
  border-radius: 8px;
  padding: 0 0.75rem;
  transition: all 0.2s;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'text'};
  overflow: hidden;
  
  &:focus-within {
    border-color: ${props => props.$hasError ? props.theme.error : props.theme.primary};
    box-shadow: 0 0 0 2px ${props => props.$hasError ? props.theme.error : props.theme.primary}20;
  }
`;

const StyledInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 0.75rem 0;
  border: none;
  background: transparent;
  font-size: 0.9375rem;
  color: ${props => props.theme.textPrimary};
  outline: none;
  
  &::placeholder {
    color: ${props => props.theme.textMuted};
  }

  &:disabled {
    cursor: not-allowed;
  }

  &::selection {
    background-color: ${props => props.theme.black};
    color: ${props => props.theme.primary};
  }

  &::-moz-selection {
    background-color: ${props => props.theme.black};
    color: ${props => props.theme.primary};
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.textMuted};
  margin-left: 0.5rem;
  flex-shrink: 0;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    color: ${props => props.theme.primary};
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  margin-top: 0.25rem;
  background-color: ${props => props.theme.surface};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 240px;
  overflow-y: auto;
`;

const DropdownItem = styled.button<{ $active?: boolean }>`
  width: 100%;
  padding: 0.75rem 1rem;
  text-align: left;
  border: none;
  background-color: ${props => props.$active ? `${props.theme.primary}15` : 'transparent'};
  color: ${props => props.$active ? props.theme.primary : props.theme.textPrimary};
  font-size: 0.9375rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${props => props.$active ? `${props.theme.primary}20` : props.theme.background};
  }

  &:first-child {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }

  &:last-child {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`;

const NoResults = styled.div`
  padding: 1rem;
  color: ${props => props.theme.textMuted};
  font-size: 0.875rem;
  text-align: center;
`;

const ErrorText = styled.small`
  font-size: 0.75rem;
  color: ${props => props.theme.error};
`;

const SearchableComboBox: React.FC<SearchableComboBoxProps> = ({
  items,
  valueId,
  onChange,
  placeholder = 'Pesquisar...',
  label,
  disabled,
  loading,
  error,
  noResultsText = 'Nenhum resultado'
}) => {
  const theme = useTheme() as any;
  const [query, setQuery] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [focused, setFocused] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const selectedLabel = useMemo(() => items.find(i => i.id === valueId)?.label || '', [items, valueId]);

  useEffect(() => {
    if (!focused) {
      setQuery('');
    }
  }, [selectedLabel, focused]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setFocused(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const displayValue = (focused || query) ? query : selectedLabel;

  const filtered = useMemo(() => {
    const q = (query || '').toLowerCase().trim();
    if (!q) return items.slice(0, 50);
    return items.filter(i => i.label.toLowerCase().includes(q)).slice(0, 50);
  }, [items, query]);

  const handleToggleOpen = () => {
    if (!disabled) {
      setOpen(prev => {
        const next = !prev;
        if (next) {
          setFocused(true);
          setQuery('');
        } else {
          setFocused(false);
        }
        return next;
      });
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setQuery('');
    if (open) setOpen(false);
    setFocused(false);
  };

  return (
    <Container ref={containerRef}>
      {label && <Label>{label}</Label>}
      <InputWrapper $disabled={disabled} $hasError={!!error} $isOpen={open}>
        {!focused && !query && <Search size={20} color={theme.textMuted} style={{ marginRight: '0.5rem' }} />}
        <StyledInput
          type="text"
          placeholder={placeholder}
          value={displayValue}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => { 
            setOpen(true); 
            setFocused(true); 
            setQuery(''); 
          }}
          disabled={disabled}
        />
        {valueId && !disabled && (
          <IconWrapper onClick={handleClear} title="Limpar seleção">
            <X size={18} />
          </IconWrapper>
        )}
        <IconWrapper 
          onClick={handleToggleOpen} 
          style={{ transform: open ? 'rotate(180deg)' : 'none' }}
        >
          <ChevronDown size={24} />
        </IconWrapper>
      </InputWrapper>

      {error && <ErrorText>{error}</ErrorText>}

      {open && !disabled && (
        <DropdownMenu>
          {loading ? (
            <NoResults>Carregando...</NoResults>
          ) : filtered.length === 0 ? (
            <NoResults>{noResultsText}</NoResults>
          ) : (
            filtered
                .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
                .map(item => (
              <DropdownItem
                type="button"
                key={item.id}
                $active={item.id === valueId}
                onClick={() => {
                  onChange(item.id);
                  setOpen(false);
                  setFocused(false);
                  setQuery('');
                }}
              >
                {item.label}
              </DropdownItem>
            ))
          )}
        </DropdownMenu>
      )}
    </Container>
  );
};

export default SearchableComboBox;
